import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-matching-user/checkAuthorizedMiddleware.js';
import { BuddyUser } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { MatchingPermission } from '@adornis/bh-matching-user/permissions.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import type { FullDataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { of, switchMap } from 'rxjs';
import { BuddyExportDataView } from '../db/BuddyExportDataView.js';

export const subscribeMatchedBuddyUsers = registerSubscription({
  operationName: 'subscribeMatchedBuddyUsers',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS)),
  ],
  type: () => [BuddyUser],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) =>
    <SelSet extends BaseQLSelectionSet>(gqlFields: SelSet) => {
      return of(1).pipe(
        switchMap(async () => {
          const userCollection = mongo().collection<BuddyUser>(BuddyUser._collectionName);
          return (await userCollection
            .aggregate([
              { $match: filter.getMongoFilter() },
              {
                $lookup: {
                  from: Match._collectionName,
                  as: 'mentee_matches',
                  localField: '_id',
                  foreignField: 'menteeID',
                },
              },
              {
                $lookup: {
                  from: Match._collectionName,
                  as: 'mentor_matches',
                  localField: '_id',
                  foreignField: 'mentorID',
                },
              },
              { $match: { $or: [{ mentee_matches: { $ne: [] } }, { mentor_matches: { $ne: [] } }] } },
              { $skip: skip },
              { $limit: limit },
            ])
            .toArray()) as BuddyUser[];
        }),
      );
    },
});

export const buddyExportDataView = async <T extends typeof AdornisEntity, Te extends typeof BuddyExportDataView>(
  dataViewOptions: FullDataViewOptions<T>,
  exportOptions?: { fileName?: string; encoding?: 'windows1252' | 'utf8'; stepSize?: number; exportClass?: Te },
) => {
  return exportDataView(dataViewOptions, { exportClass: BuddyExportDataView, ...exportOptions });
};

export namespace BuddyExportAPI {
  export const exportData = buddyExportDataView;
}
